import type { AvailableMember } from "@/hooks/useAvailbleMember";
import type { AvailableMemberSearchBy } from "@/hooks/useAvailbleMember";
import { TableCell, TableRow } from "@/components/ui/table";
import { cn } from "@/utils/cn";
import dayjs from "dayjs";
import type { ReactNode } from "react";
import { Check } from "lucide-react";
import { formatExperienceYears } from "@/utils/experience";

interface AvailableMemberRowProps {
  member: AvailableMember;
  index: number;
  isHighlighted?: boolean;
  isSelected?: boolean;
  searchTerm?: string;
  searchBy?: AvailableMemberSearchBy;
  onToggleSelection?: (nurseId: string) => void;
  onRowClick?: (e: React.MouseEvent) => void;
}

/**
 * 메디통 병원 근무자 전용 Row 컴포넌트
 * No. 사번 이름 직종 성별 경력 입사일 생년월일 연락처 소속만 표시
 */
export function AvailableMemberRow({
  member,
  index,
  isHighlighted = false,
  isSelected = false,
  searchTerm = "",
  searchBy = "name",
  onToggleSelection,
  onRowClick
}: AvailableMemberRowProps) {
  const formattedBirthDate = member.birth_date
    ? dayjs(member.birth_date).format("YYYY-MM-DD")
    : "-";
  const formattedJoiningDate = member.joining_date
    ? dayjs(member.joining_date).format("YYYY-MM-DD")
    : "-";
  const normalizedSearchTerm = searchTerm.trim();
  const getAffiliation = () => {
    const values = [
      member.small_kind_name,
      member.mb_part_name,
      member.middle_kind_name,
      member.big_kind_name,
    ].filter(Boolean);

    if (searchBy === "affiliation" && normalizedSearchTerm) {
      const matched = values.find((value) =>
        String(value).includes(normalizedSearchTerm)
      );
      if (matched) return matched;
    }

    return values[0] || "-";
  };

  const affiliation = getAffiliation();

  const renderHighlightedText = (value: string | null | undefined): ReactNode => {
    const text = value || "-";
    if (!normalizedSearchTerm || text === "-") return text;

    const parts = text.split(normalizedSearchTerm);
    if (parts.length === 1) return text;

    return parts.map((part, idx) => (
      <span key={`${part}-${idx}`}>
        {part}
        {idx < parts.length - 1 ? (
          <mark className="rounded-[3px] bg-[#FFF0A6] px-[2px] font-pretendard font-semibold text-[#1F2937]">
            {normalizedSearchTerm}
          </mark>
        ) : null}
      </span>
    ));
  };

  // 타 병동에 이미 등록된 근무자는 선택 불가(병동이동 기능으로 분리).
  const isRegistered = Boolean(member.is_registered);

  const handleClick = (e: React.MouseEvent) => {
    if (isRegistered) return; // 등록자는 선택 차단
    if (onRowClick) {
      onRowClick(e); // Table에서 shift 로직 처리
    } else {
      onToggleSelection?.(member.nurse_id); // fallback
    }
  };

  return (
    <TableRow
      className={cn(
        "bg-white group transition-colors select-none",
        isRegistered
          ? "cursor-not-allowed bg-[#F3F4F6] text-[#9CA3AF]"
          : "cursor-pointer",
        isHighlighted && !isRegistered && "bg-blue-100",
        isSelected && !isRegistered && "!bg-[#D4F0FF]"
      )}
      data-nid={member.nurse_id}
      aria-disabled={isRegistered || undefined}
      onClick={handleClick}
    // onClick={() => onToggleSelection?.(member.nurse_id)}
    >
      {/* 선택 */}
      <TableCell className="text-center">
        <span
          className={cn(
            "mx-auto flex h-[18px] w-[18px] items-center justify-center rounded-[5px] border transition-colors",
            isRegistered
              ? "border-[#D6D6DE] bg-[#E5E7EB]"
              : isSelected
                ? "border-[#0E59C6] bg-[#0E59C6]"
                : "border-[#C9CDD7] bg-white group-hover:border-[#0E59C6]"
          )}
        >
          {isSelected && !isRegistered ? (
            <Check className="h-3.5 w-3.5 text-white" />
          ) : null}
        </span>
      </TableCell>
      {/* No. */}
      <TableCell className="text-center">{index + 1}</TableCell>
      {/* 사번 */}
      <TableCell className="text-center">{member.emp_num || "-"}</TableCell>
      {/* 이름 */}
      <TableCell className="text-center">
        <div className="flex flex-col items-center justify-center gap-0.5">
          <span>
            {searchBy === "name"
              ? renderHighlightedText(member.name)
              : member.name || "-"}
          </span>
          {isRegistered ? (
            <span className="inline-flex items-center rounded-full bg-[#E5E7EB] px-2 py-0.5 text-[11px] font-medium text-[#6B7280]">
              {member.registered_group_name
                ? `${member.registered_group_name} 소속`
                : "타 병동 소속"}
            </span>
          ) : null}
        </div>
      </TableCell>
      {/* 소속 */}
      <TableCell className="text-center">
        {searchBy === "affiliation"
          ? renderHighlightedText(affiliation)
          : affiliation}
      </TableCell>
      {/* 직종 */}
      <TableCell className="text-center">{member.duty || "-"}</TableCell>
      {/* 성별 */}
      <TableCell className="text-center">
        {member.gender === "M" || member.gender === "남"
          ? "남"
          : member.gender === "F" || member.gender === "여"
            ? "여"
            : member.gender || "-"}
      </TableCell>
      {/* 경력 */}
      <TableCell className="text-center">
        {formatExperienceYears(member.career)}
      </TableCell>
      {/* 입사일 */}
      <TableCell className="text-center">{formattedJoiningDate}</TableCell>
      {/* 생년월일 */}
      <TableCell className="text-center">{formattedBirthDate}</TableCell>
      {/* 연락처 */}
      <TableCell className="text-center">
        {member.phone_number || "-"}
      </TableCell>
    </TableRow>
  );
}
